/**
 * Gate inbox — residents' notes to the desk. Tap marks as seen (flips the
 * SEEN pill on the resident's side).
 */
import { router } from 'expo-router';
import React from 'react';
import { Pressable, View } from 'react-native';

import { space } from '@bero/tokens';
import { bool, str, useGateList } from '@/components/gate/api';
import { Icon } from '@/components/gate/icons';
import { useT } from '@/i18n';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, Card, EmptyState, Pill, Screen, Skeleton, TopBar } from '@/ui';

type Msg = { id: string; body: string; flat: string; read: boolean; at: string };

function toMsg(m: unknown, i: number): Msg {
  const iso = str(m, 'createdAt') ?? '';
  const d = new Date(iso);
  let at = '';
  if (!Number.isNaN(d.getTime())) {
    let h = d.getHours(); const am = h < 12 ? 'AM' : 'PM'; h = h % 12 || 12;
    at = `${h}:${String(d.getMinutes()).padStart(2, '0')} ${am}`;
  }
  return {
    id: str(m, 'id') ?? `m${i}`,
    body: str(m, 'body') ?? '',
    flat: str(m, 'flatCode') ?? '',
    read: bool(m, 'readByGuard') ?? false,
    at,
  };
}

export default function GuardMessages() {
  const { colors } = useTheme();
  const { t } = useT();
  const { items, setItems, state } = useGateList<unknown>('/api/gate/messages', 'messages');
  const rows = items.map(toMsg);

  const markRead = async (m: Msg) => {
    if (m.read) return;
    setItems(items.map((raw) => (str(raw, 'id') === m.id ? { ...(raw as object), readByGuard: true } : raw)));
    try {
      await api.post<unknown>(`/api/gate/messages/${encodeURIComponent(m.id)}/read`, {});
    } catch { /* optimistic; refetch next visit */ }
  };

  return (
    <Screen scroll={false}>
      <TopBar title={t('msgs_title')} onBack={() => router.back()} />
      <Screen padded style={{ paddingTop: space.s4 }}>
        {state === 'loading' ? (
          <View style={{ gap: space.s3 }}>{[0, 1, 2].map((i) => <Skeleton key={i} h={72} />)}</View>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Icon name="mail" size={40} color={colors.textTertiary} />}
            title={t('msgs_empty_title')}
            body={t('msgs_empty_body')}
          />
        ) : (
          <View style={{ gap: space.s3 }}>
            {rows.map((m) => (
              <Pressable key={m.id} accessibilityRole="button" onPress={() => void markRead(m)}>
                <Card style={!m.read ? { borderWidth: 1.5, borderColor: colors.primary400 } : undefined}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2, marginBottom: 6 }}>
                    <AppText variant="bodySm" weight="700">{m.flat}</AppText>
                    <AppText variant="bodySm" tone="tertiary">{m.at}</AppText>
                    <View style={{ flex: 1 }} />
                    <Pill status={m.read ? 'open' : 'approved'} label={m.read ? 'SEEN' : 'NEW'} />
                  </View>
                  <AppText variant="bodyMd">{m.body}</AppText>
                </Card>
              </Pressable>
            ))}
          </View>
        )}
      </Screen>
    </Screen>
  );
}
